'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Typography } from '../../../components';
import CampaignsTable from './components/CampaignsTable';
import FiltersSection from './components/FiltersSection';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import { CampaignsFilters, AvailableModel } from './types';
import clientRevshareService, { RevshareOrder } from '../../../api/client/clientRevshareService';
import clientAccService from '../../../api/client/clientAccService';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { useTranslations } from '../../../contexts/translation.context';
import { showError } from '../../../utils/toast';

const RevshareCampaignsPage: React.FC = () => {
  const { t } = useTranslations();
  const [campaigns, setCampaigns] = useState<RevshareOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Filters state
  const [filters, setFilters] = useState<CampaignsFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');
  const [localFansFrom, setLocalFansFrom] = useState('');
  const [localRevenueFrom, setLocalRevenueFrom] = useState('');

  // Ref to store abort controller for cancelling previous requests
  const abortControllerRef = useRef<AbortController | null>(null);
  // Ref to store translation function to avoid recreating loadCampaigns
  const tRef = useRef(t);

  // Update ref when t changes
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const breadcrumbs = useMemo(
    () => [
      { label: t('revshare.home'), href: '/' },
      { label: t('revshare.campaigns'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  // Check if there are any active filters (compare with DEFAULT_FILTERS)
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search.trim() !== DEFAULT_FILTERS.search ||
      filters.sort !== DEFAULT_FILTERS.sort ||
      filters.model !== DEFAULT_FILTERS.model ||
      (filters.status ?? DEFAULT_FILTERS.status) !== DEFAULT_FILTERS.status ||
      filters.source !== DEFAULT_FILTERS.source ||
      filters.campaignType !== DEFAULT_FILTERS.campaignType ||
      filters.fansFrom !== DEFAULT_FILTERS.fansFrom ||
      filters.revenueFrom !== DEFAULT_FILTERS.revenueFrom
    );
  }, [filters]);

  // Load available models
  const fetchAvailableModels = useCallback(async (search: string = '') => {
    setModelsLoading(true);
    try {
      const response = await clientAccService.getAccounts({
        page: 1,
        page_size: 50,
        search: search,
      });
      if (response.data && response.data.objects) {
        setAvailableModels(
          response.data.objects.map((acc: any) => ({
            id: acc._id || acc.id,
            name: acc.name,
            username: acc.of_user_name || acc.username, // Adjust based on API response
            avatar: acc.avatar,
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Load models only once on mount
  useEffect(() => {
    fetchAvailableModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModelSearchChange = useCallback(
    (value: string) => {
      fetchAvailableModels(value);
    },
    [fetchAvailableModels]
  );

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        // Only update if value actually changed
        if (prev.search !== localSearch) {
          return { ...prev, search: localSearch };
        }
        return prev;
      });
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Debounced fansFrom
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = localFansFrom === '' ? null : parseInt(localFansFrom, 10);
      setFilters((prev) => {
        // Only update if value actually changed
        if (prev.fansFrom !== value) {
          return { ...prev, fansFrom: value };
        }
        return prev;
      });
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localFansFrom]);

  // Debounced revenueFrom
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = localRevenueFrom === '' ? null : parseInt(localRevenueFrom, 10);
      setFilters((prev) => {
        // Only update if value actually changed
        if (prev.revenueFrom !== value) {
          return { ...prev, revenueFrom: value };
        }
        return prev;
      });
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localRevenueFrom]);

  const loadCampaigns = useCallback(async () => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };

      // search -> id (backend field name)
      if (filters.search) params.search = filters.search;

      // sort_by - use directly from filters.sort (already contains - prefix for desc)
      if (filters.sort !== 'all') {
        params.sort_by = filters.sort;
      }

      // models filter (backend expects 'models' param)
      if (filters.model !== 'all') params.models = filters.model;

      // status filter
      if (filters.status !== 'all') params.status = filters.status;

      // source filter
      if (filters.source !== 'all') params.source = filters.source;

      // model_type filter (free/free trial)
      // API only accepts 'free' or 'free trial', not 'paid'
      if (filters.campaignType !== 'all') {
        if (filters.campaignType === 'free_trial') {
          params.model_type = 'free trial';
        } else if (filters.campaignType === 'free') {
          params.model_type = 'free';
        }
        // If 'paid' is selected, don't send model_type (API doesn't support it)
      }

      // subs_from - minimum subscribers
      if (filters.fansFrom !== null) params.subs_from = filters.fansFrom;

      // income - minimum revenue
      if (filters.revenueFrom !== null) params.income = filters.revenueFrom;

      const response = await clientRevshareService.getOrders(params);

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      if (response && response.objects && Array.isArray(response.objects)) {
        setCampaigns(response.objects);
        setTotalCount(response.count || 0);
      } else {
        setCampaigns([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      // Don't show error if request was aborted
      if (error?.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }
      console.error('Error loading revshare campaigns:', error);
      showError(tRef.current('campaigns.errors.loadFailed'));
      setCampaigns([]);
      setTotalCount(0);
    } finally {
      // Only update loading state if this request wasn't aborted
      // If aborted, the new request will set loading state
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [currentPage, pageSize, filters]);

  // Load campaigns when dependencies change
  useEffect(() => {
    loadCampaigns();

    // Cleanup: cancel request on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadCampaigns]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback(
    (key: keyof CampaignsFilters, value: string | string[] | number | null) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setLocalFansFrom('');
    setLocalRevenueFrom('');
    setCurrentPage(1);
  }, []);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-6 md:py-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('revshare.campaigns')}
        </Typography>
      </div>

      <FiltersSection
        filters={filters}
        localSearch={localSearch}
        localFansFrom={localFansFrom}
        localRevenueFrom={localRevenueFrom}
        hasActiveFilters={hasActiveFilters}
        availableModels={availableModels}
        modelsLoading={modelsLoading}
        onModelSearchChange={handleModelSearchChange}
        onSearchChange={setLocalSearch}
        onFansFromChange={setLocalFansFrom}
        onRevenueFromChange={setLocalRevenueFrom}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
      />

      <div className="px-4 md:px-6 md:h-full">
        <CampaignsTable
          campaigns={campaigns}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
};

export default RevshareCampaignsPage;
